import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Divider,
  Alert,
} from '@mui/material';
import {
  PersonOff as WithdrawnIcon,
  PersonAdd as RestoreIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { loadWithdrawnSkaters, formatWithdrawalDate } from '../services/withdrawalService';

export default function WithdrawalManager({ 
  open, 
  onClose, 
  level, 
  skaters = [], // Provide default empty array
  season = null,
  onWithdraw, 
  onRestore, 
  onUpdateNote 
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAdmin } = useAuth();
  
  const [withdrawnSkaters, setWithdrawnSkaters] = useState([]);
  const [selectedSkater, setSelectedSkater] = useState(null);
  const [withdrawalNote, setWithdrawalNote] = useState('');
  const [editNoteOpen, setEditNoteOpen] = useState(false);
  const [editingSkater, setEditingSkater] = useState(null);
  const [editNote, setEditNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadWithdrawnSkatersForLevel = useCallback(async () => {
    if (!level) return;
    
    try {
      setLoading(true);
      setError(null);
      const withdrawn = await loadWithdrawnSkaters(level, season);
      setWithdrawnSkaters(withdrawn || []);
    } catch (err) {
      console.error('Error loading withdrawn skaters:', err);
      setError(err.message || 'Failed to load withdrawn skaters');
      setWithdrawnSkaters([]);
    } finally {
      setLoading(false);
    }
  }, [level, season]);

  useEffect(() => {
    if (open && level) {
      loadWithdrawnSkatersForLevel();
    }
  }, [open, level, loadWithdrawnSkatersForLevel]);

  const handleWithdraw = async () => {
    if (!selectedSkater || !withdrawalNote.trim()) return;
    
    try {
      await onWithdraw(selectedSkater, withdrawalNote);
      setWithdrawnSkaters(prev => [...prev, { ...selectedSkater, withdrawalNote, withdrawnAt: new Date() }]);
      setSelectedSkater(null);
      setWithdrawalNote('');
      onClose();
    } catch (err) {
      console.error('Error withdrawing skater:', err);
      setError(err.message || 'Failed to withdraw skater');
    }
  };

  const handleRestore = async (skater) => {
    try {
      await onRestore(skater);
      setWithdrawnSkaters(prev => prev.filter(s => s.skaterId !== skater.skaterId));
    } catch (err) {
      console.error('Error restoring skater:', err);
      setError(err.message || 'Failed to restore skater');
    }
  };

  const handleEditNote = async () => {
    if (!editingSkater || !editNote.trim()) return;
    
    try {
      await onUpdateNote(editingSkater, editNote);
      setWithdrawnSkaters(prev => prev.map(s => 
        s.skaterId === editingSkater.skaterId ? { ...s, withdrawalNote: editNote } : s
      ));
      setEditNoteOpen(false);
      setEditingSkater(null);
      setEditNote('');
    } catch (err) {
      console.error('Error updating note:', err);
      setError(err.message || 'Failed to update note');
    }
  };

  const openEditNote = (skater) => {
    setEditingSkater(skater);
    setEditNote(skater.withdrawalNote || '');
    setEditNoteOpen(true);
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WithdrawnIcon />
            Withdrawal Management - {level}
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Mark Skater as Withdrawn
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a skater to mark as withdrawn. Withdrawn skaters are hidden from public view.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              {skaters && skaters.length > 0 ? (
                skaters.map((skater) => (
                  <Chip
                    key={skater.name || `${skater.firstName}-${skater.lastName}`}
                    label={skater.name || `${skater.firstName} ${skater.lastName}`}
                    variant={selectedSkater?.name === skater.name ? "filled" : "outlined"}
                    color={selectedSkater?.name === skater.name ? "primary" : "default"}
                    onClick={() => setSelectedSkater(skater)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No skaters available for this level.
                </Typography>
              )}
            </Box>
            
            {selectedSkater && (
              <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Selected: {selectedSkater.name || `${selectedSkater.firstName} ${selectedSkater.lastName}`}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Withdrawal Note (optional)"
                  value={withdrawalNote}
                  onChange={(e) => setWithdrawalNote(e.target.value)}
                  placeholder="Reason for withdrawal, contact info, etc."
                  size="small"
                />
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Currently Withdrawn Skaters
            </Typography>
            {loading ? (
              <Typography variant="body2" color="text.secondary">
                Loading withdrawn skaters...
              </Typography>
            ) : withdrawnSkaters.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No skaters are currently withdrawn from this level.
              </Typography>
            ) : (
              <TableContainer component={Paper} elevation={1}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Skater</TableCell>
                      <TableCell>Club</TableCell>
                      <TableCell>Withdrawn Date</TableCell>
                      <TableCell>Note</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {withdrawnSkaters.map((skater) => (
                      <TableRow key={skater.skaterId || skater.id || skater.name}>
                        <TableCell>
                          {skater.skaterName || skater.name || `${skater.firstName} ${skater.lastName}`}
                        </TableCell>
                        <TableCell>{skater.club || 'N/A'}</TableCell>
                        <TableCell>
                          {formatWithdrawalDate(skater.withdrawnAt)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 200 }}>
                            {skater.withdrawalNote || 'No note'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Edit note">
                              <IconButton 
                                size="small" 
                                onClick={() => openEditNote(skater)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Restore skater">
                              <IconButton 
                                size="small" 
                                color="success"
                                onClick={() => handleRestore(skater)}
                              >
                                <RestoreIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>
            Close
          </Button>
          {selectedSkater && (
            <Button 
              onClick={handleWithdraw} 
              variant="contained" 
              color="warning"
              startIcon={<WithdrawnIcon />}
              disabled={!withdrawalNote.trim()}
            >
              Mark as Withdrawn
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Note Dialog */}
      <Dialog open={editNoteOpen} onClose={() => setEditNoteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Withdrawal Note</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Update the withdrawal note for {editingSkater?.skaterName || editingSkater?.name || `${editingSkater?.firstName} ${editingSkater?.lastName}`}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Withdrawal Note"
            value={editNote}
            onChange={(e) => setEditNote(e.target.value)}
            placeholder="Reason for withdrawal, contact info, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditNoteOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleEditNote} 
            variant="contained"
            disabled={!editNote.trim()}
          >
            Update Note
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
